import React from "react"
import { ApolloConsumer } from "react-apollo"
import gql from "graphql-tag"
import uuid from "uuid/v4"

class Container extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      selectedTile: {
        component: null,
        result: null,
        parentId: null,
      },
      selectedGrid: {
        component: null,
        parentId: null,
      },
      error: null,
    }

    this.typingTimer
  }

  static getDerivedStateFromProps = (props, state) => {
    return {
      ...state,
      components: props.components,
    }
  }

  onChangeText = (id) => (e) => {
    e.persist()

    this.props.updateComponent(id, {
      [e.target.name]: e.target.value,
    })
  }

  openTileModal = (component, parentId) => {
    this.setState({
      selectedTile: {
        component,
        parentId,
        result: null,
      },
    })
  }

  closeModal = () => {
    this.setState({
      selectedTile: {
        component: null,
        result: null,
        parentId: null,
      },
      selectedGrid: {
        component: null,
        parentId: null,
      },
      loading: false,
      error: null,
    })
  }

  saveGrid = () => {
    let { component, parentId } = this.state.selectedGrid

    if (parentId) {
      this.props.addComponent(parentId, component)
    } else {
      this.props.updateComponent(component.id, {
        class: component.class,
      })
    }

    this.closeModal()
  }

  saveTile = () => {
    let {
      selectedTile: { component, parentId },
      error,
      loading,
    } = this.state

    if (loading || error) return null

    let productRegex = /^\/marketplace\/([a-zA-Z0-9\-]+)(\/view)?$/i
    let topicRegex = /^\/(vendor|sector)\/([a-zA-Z0-9\-]+)$/i
    let postRegex = /^\/(articles|newsletters)\/([a-zA-Z0-9\-]+)$/i

    if (!component.link) {
      error = "Link is required"
    } else if (
      !productRegex.test(component.link) &&
      !topicRegex.test(component.link) &&
      !postRegex.test(component.link)
    ) {
      error = `Please input a valid vendor, sector, post or report url. Make sure you include the leading forward slash (e.g. "/marketplace/aws-tsis", "/sector/container-orchestration")`
    } else if (!component.image) {
      error = "Image link is required"
    }

    if (error) {
      this.setState({ error })
      return
    }

    if (parentId) {
      this.props.addComponent(parentId, component)
    } else {
      this.props.updateComponent(component.id, {
        link: component.link,
        image: component.image || "",
      })
    }
    this.closeModal()
  }

  onChangeSelectedLink = (e) => {
    e.persist()

    clearTimeout(this.typingTimer)

    this.setState(
      ({ selectedTile }) => ({
        selectedTile: {
          ...selectedTile,
          component: {
            ...selectedTile.component,
            image: null || "",
            link: e.target.value,
          },
        },
        loading: false,
        error: null,
      }),
      () => {
        this.typingTimer = setTimeout(
          () => this.checkSelectedLink(e.target.value),
          700
        )
      }
    )
  }

  onChangeSelectedImage = (e) => {
    e.persist()

    this.setState(({ selectedTile }) => ({
      selectedTile: {
        ...selectedTile,
        component: {
          ...selectedTile.component,
          image: e.target.value || "",
        },
      },
    }))
  }

  checkSelectedLink = async (link) => {
    let productRegex = /^\/marketplace\/([a-zA-Z0-9\-]+)(\/view)?$/i
    let topicRegex = /^\/(vendor|sector)\/([a-zA-Z0-9\-]+)$/i
    let postRegex = /^\/(articles|newsletters)\/([a-zA-Z0-9\-]+)$/i

    if (!link) return

    let isProduct = productRegex.test(link)
    let isTopic = topicRegex.test(link)
    let isPost = postRegex.test(link)

    if (!isProduct && !isTopic && !isPost) {
      this.setState({
        loading: false,
        error: `Please input a valid vendor, sector, post or report url. Make sure you include the leading forward slash (e.g. "/marketplace/aws-tsis", "/sector/container-orchestration")`,
      })
    }

    let type, slug

    if (isProduct) {
      let match = productRegex.exec(link)
      type = "product"
      slug = match[1]
    } else if (isTopic) {
      let match = topicRegex.exec(link)
      type = match[1]
      slug = match[2]
    } else if (isPost) {
      type = "post"
      slug = link
    }

    this.setState({ loading: true })

    let response = await this.props
      .fetchProductOrTopic(slug, type)
      .catch((error) => ({ error }))

    if (response.error) {
      this.setState({
        loading: false,
        error: "Not Found",
      })
      return
    } else {
      let image
      switch (type.toLowerCase()) {
        case "post":
          image = response.data.page.seo.image
          break
        case "vendor":
        case "sector":
        case "product":
          image = response.data.page.logo
          break
      }

      this.setState(({ selectedTile }) => ({
        selectedTile: {
          ...selectedTile,
          result: response.data.page,
          component: {
            ...selectedTile.component,
            image: image || "",
          },
        },
        loading: false,
        error: null,
      }))
    }
  }

  addComponent = (parentId, type) => {
    let newComponent = {
      type,
      id: uuid(),
    }

    if (type === "grid" || type === "container") {
      newComponent.title = ""
      newComponent.subtitle = ""
      newComponent.components = []
      newComponent.class = type === "grid" ? "four-x-two-list" : undefined
    } else if (type === "tile") {
      newComponent.link = ""
      newComponent.image = ""
    }

    if (type === "container") {
      this.props.addComponent(parentId, newComponent)
    } else if (type === "grid") {
      this.setState({
        selectedGrid: {
          component: newComponent,
          parentId,
        },
      })
    } else if (type === "tile") {
      this.setState({
        selectedTile: {
          component: newComponent,
          result: null,
          parentId,
        },
      })
    }
  }

  onChangeGridLayout = (e) => {
    e.persist()

    this.setState(({ selectedGrid }) => ({
      selectedGrid: {
        ...selectedGrid,
        component: {
          ...selectedGrid.component,
          class: e.target.value,
        },
      },
    }))
  }

  _renderContainer = (component) => {
    let { components, class: componentClass, type, id } = component

    let maxLength

    switch (componentClass) {
      case "four-x-two-list":
        maxLength = 8
        components = components.slice(0, 8)
        break
      case "four-x-one-list":
      case "two-x-two-list":
        maxLength = 4
        components = components.slice(0, 4)
        break
      case "two-x-three-list":
        maxLength = 6
        components = components.slice(0, 6)
        break
      case undefined:
      case null:
        maxLength = components.length + 1
        break
      default:
        maxLength = 0
        components = []
    }

    let addType

    switch (type) {
      case "grid":
        addType = "tile"
        break
      case "container":
        addType = "grid"
        break
      default:
        addType = "container"
        break
    }

    let numEmpty = maxLength - components.length

    return (
      <div className={`hp-item-container ${componentClass}`}>
        {components.map(this._renderComponent)}
        {numEmpty > 0 && this._renderEmptyComponents(id, numEmpty, addType)}
      </div>
    )
  }

  _renderEmptyComponents = (parentId, n, type) => {
    let emptyComponents = []

    for (let i = 0; i < n; i++) {
      emptyComponents.push(
        <div
          key={i}
          onClick={() => this.addComponent(parentId, type)}
          className={`hp-item type-${type || "none"} ${i === 0 && "addable"}`}
        >
          {i === 0 ? "+ ADD" : ""}
        </div>
      )
    }

    return emptyComponents
  }

  _renderComponent = (component, i, parentArray) => {
    let { id, type, image, title, subtitle, components } = component
    return (
      <div key={i} className={`hp-item type-${type}`}>
        {type !== "tile" && (
          <div className="edit-options">
            {i > 0 && (
              <div
                className="option-button move-up"
                onClick={() => this.props.moveComponent(component.id, -1)}
              >
                Move Up
              </div>
            )}
            {i < parentArray.length - 1 && (
              <div
                className="option-button move-down"
                onClick={() => this.props.moveComponent(component.id, 1)}
              >
                Move Down
              </div>
            )}
            <div
              className="option-button delete-button"
              onClick={() => this.props.deleteComponent(component.id)}
            >
              Delete
            </div>
          </div>
        )}
        {type !== "tile" && (
          <div className="title-subtitle">
            <input
              placeholder="Title"
              value={title}
              name="title"
              onChange={this.onChangeText(id)}
            />
            <input
              placeholder="Subtitle"
              value={subtitle}
              name="subtitle"
              onChange={this.onChangeText(id)}
            />
          </div>
        )}
        {image && (
          <div
            className="content-image"
            style={{ backgroundImage: `url("${image}")` }}
          />
        )}
        {components && this._renderContainer(component, i)}
        {type === "tile" && (
          <div className="hover-overlay">
            <div
              className="overlay-option"
              onClick={() => this.openTileModal(component)}
            >
              Edit
            </div>
            <div
              className="overlay-option delete"
              onClick={() => this.props.deleteComponent(component.id)}
            >
              Delete
            </div>
          </div>
        )}
      </div>
    )
  }

  _renderSelectedResult = () => {
    let { component, result } = this.state.selectedTile

    if (this.state.loading) {
      return <div className="loading">Searching...</div>
    }

    if (result) {
      let image =
        !result.logo && !result.seo
          ? false
          : !result.logo
          ? result.seo.image
          : result.logo
      return (
        <div className="selected-tile-details">
          <span>
            {result.name}{" "}
            {!result.isForSale && !result.seo
              ? "(not for sale on marketplace)"
              : ""}
          </span>
          <div className="hp-item type-tile">
            <div
              className="content-image"
              style={{ backgroundImage: `url("${component.image}")` }}
            />
          </div>
          {!image && (
            <div className="input-item">
              <span>No image found! Input an image link: </span>
              <input
                value={component.image}
                onChange={this.onChangeSelectedImage}
                size={25}
              />
            </div>
          )}
        </div>
      )
    }

    return null
  }

  _renderEditTileModal = () => {
    let {
      selectedTile: { component, result, parentId },
      loading,
      error,
    } = this.state

    if (!component) return null

    let canSubmit = !loading && !error && result && component.image

    return (
      <div className="edit-modal">
        <div className="edit-modal-body">
          <h4>{parentId ? "Add" : "Edit"} Tile</h4>
          <div className="input-item">
            <span style={{ display: "block" }}>https://etr.plus</span>
            <input
              value={component.link}
              placeholder="/<marketplace|vendor|sector|articles|newsletters>/<product or topic>"
              onChange={this.onChangeSelectedLink}
              size={50}
            />
          </div>
          <div className="input-item">
            <span style={{ display: "block" }}>Image:</span>
            <input
              value={component.image}
              placeholder="image url"
              onChange={this.onChangeSelectedImage}
              size={50}
            />
          </div>
          {this._renderSelectedResult()}
          {error && <div className="error">{error}</div>}
          <div className="modal-button-container">
            <div
              className="button-item cancel"
              onClick={() => this.closeModal()}
            >
              Cancel
            </div>
            <div
              className={`button-item submit ${!canSubmit ? "disabled" : ""}`}
              onClick={() => this.saveTile()}
            >
              Submit
            </div>
          </div>
        </div>
      </div>
    )
  }

  _renderGridLayout = () => {
    let { component } = this.state.selectedGrid

    if (!component) return null

    let n

    switch (component.class) {
      case "four-x-two-list":
        n = 8
        break
      case "four-x-one-list":
      case "two-x-two-list":
        n = 4
        break
      case "two-x-three-list":
        n = 6
        break
      default:
        n = 0
        break
    }

    let items = []

    for (let i = 0; i < n; i++) {
      items.push(<div className="hp-item type-tile" key={i} />)
    }

    return <div className={`hp-item-container ${component.class}`}>{items}</div>
  }

  _renderEditGridModal = () => {
    // TODO: select layout
    let { component } = this.state.selectedGrid

    if (!component) return null

    let options = [
      { text: "4x2", value: "four-x-two-list" },
      { text: "4x1", value: "four-x-one-list" },
      { text: "2x2", value: "two-x-two-list" },
      { text: "2x3", value: "two-x-three-list" },
    ]

    return (
      <div className="edit-modal">
        <div className="edit-modal-body">
          <h4>Select Grid Layout</h4>
          <div className="grid-layout-options">
            <select
              value={component.class || undefined}
              onChange={this.onChangeGridLayout}
            >
              {options.map(({ text, value }, i) => (
                <option key={i} value={value}>
                  {text}
                </option>
              ))}
            </select>
          </div>
          {this._renderGridLayout()}
          <div className="modal-button-container">
            <div
              className="button-item cancel"
              onClick={() => this.closeModal()}
            >
              Cancel
            </div>
            <div
              className="button-item submit"
              onClick={() => this.saveGrid()}
            >
              Submit
            </div>
          </div>
        </div>
      </div>
    )
  }

  render = () => {
    return (
      <div>
        {this._renderContainer(this.state)}
        {this._renderEditTileModal()}
        {this._renderEditGridModal()}
      </div>
    )
  }
}

const GET_PAGE_FOR_HOMEPAGE = gql`
  query GET_PAGE_FOR_HOMEPAGE($slug: String!, $type: String) {
    page(slug: $slug, type: $type) {
      ... on Topic {
        id
        name
        logo
      }
      ... on Product {
        id
        name
        logo
        isForSale
      }
      ... on Post {
        id
        title
        seo
      }
    }
  }
`

const ContainerWithData = (props) => (
  <ApolloConsumer>
    {(client) => {
      const fetchProductOrTopic = (slug, type) =>
        client.query({
          query: GET_PAGE_FOR_HOMEPAGE,
          fetchPolicy: "network-only",
          variables: {
            slug,
            type: type.toLowerCase(),
          },
        })
      return <Container {...props} fetchProductOrTopic={fetchProductOrTopic} />
    }}
  </ApolloConsumer>
)

export default ContainerWithData
